import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import type { RidePoint } from "../types/ride";
import { useTheme } from "../theme/ThemeProvider";

interface MetricDef {
  key: string;
  label: string;
  color: string;
  pick: (p: RidePoint) => number;
}

const METRICS: MetricDef[] = [
  { key: "speed", label: "Speed (km/h)", color: "#0398f0", pick: (p) => p.speed * 3.6 },
  { key: "power", label: "Power (W)", color: "#f59e0b", pick: (p) => p.power },
  { key: "battery", label: "Battery (%)", color: "#22c55e", pick: (p) => p.battery ?? NaN },
  { key: "voltage", label: "Voltage (V)", color: "#eab308", pick: (p) => p.voltage },
  { key: "currentMotor", label: "Motor current (A)", color: "#a855f7", pick: (p) => p.currentMotor },
  { key: "currentIn", label: "Battery current (A)", color: "#ec4899", pick: (p) => p.currentIn },
  { key: "dutyCycle", label: "Duty cycle (%)", color: "#06b6d4", pick: (p) => p.dutyCycle * 100 },
  { key: "rpm", label: "Motor RPM", color: "#84cc16", pick: (p) => p.rpm },
  { key: "tempMotor", label: "Motor temp (°C)", color: "#ef4444", pick: (p) => p.tempMotor },
  { key: "tempMosfet", label: "MOSFET temp (°C)", color: "#e11d48", pick: (p) => p.tempMosfet },
  { key: "elevation", label: "Elevation (m)", color: "#78716c", pick: (p) => p.ele ?? NaN },
  { key: "distanceM", label: "Distance (m)", color: "#6366f1", pick: (p) => p.distanceM },
  { key: "whUsed", label: "Energy used (Wh)", color: "#14b8a6", pick: (p) => p.whUsed },
  { key: "ahUsed", label: "Charge used (Ah)", color: "#f97316", pick: (p) => p.ahUsed },
];

const DEFAULT_KEYS = ["speed", "power"];

function formatMinSec(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function RideChart({
  points,
  cursorIndex,
  onCursorChange,
}: {
  points: RidePoint[];
  cursorIndex: number;
  onCursorChange: (index: number) => void;
}) {
  const { resolved } = useTheme();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(DEFAULT_KEYS));
  const [zoomed, setZoomed] = useState(false);

  function toggleMetric(key: string) {
    setSelected((prev) => {
      // Always keep at least one metric plotted.
      if (prev.has(key) && prev.size === 1) return prev;
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Stable order (matches METRICS) regardless of the order metrics were toggled in.
  const activeMetrics = useMemo(() => METRICS.filter((m) => selected.has(m.key)), [selected]);
  const activeKey = activeMetrics.map((m) => m.key).join(",");

  const containerRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const onCursorChangeRef = useRef(onCursorChange);
  onCursorChangeRef.current = onCursorChange;
  const lastEmittedRef = useRef<number | null>(null);

  const data = useMemo<uPlot.AlignedData>(() => {
    const xs = new Float64Array(points.length);
    for (let i = 0; i < points.length; i++) xs[i] = points[i]!.t / 1000;
    const series = activeMetrics.map((m) => {
      const ys = new Float64Array(points.length);
      for (let i = 0; i < points.length; i++) ys[i] = m.pick(points[i]!);
      return ys;
    });
    return [xs, ...series];
  }, [points, activeMetrics]);

  const axisColor = resolved === "dark" ? "#9a9a9a" : "#475569";
  const gridColor = resolved === "dark" ? "#2e2e2e" : "#e2e8f0";

  useEffect(() => {
    if (!containerRef.current) return;

    const width = containerRef.current.clientWidth || 600;
    const first = activeMetrics[0];
    const opts: uPlot.Options = {
      width,
      height: 220,
      padding: [8, 8, 0, 0],
      scales: { x: { time: false } },
      cursor: {
        x: true,
        y: false,
        points: { show: true },
      },
      legend: { show: true, live: true },
      axes: [
        { stroke: axisColor, grid: { stroke: gridColor }, values: (_u, ticks) => ticks.map(formatMinSec) },
        // Only the first selected metric gets a drawn axis (its own color, for orientation) -
        // the rest still plot correctly, each auto-scaled to its own range, but rely on the
        // legend below (and the color-matched toggle buttons) rather than a cluttered axis.
        ...(first ? [{ stroke: first.color, grid: { stroke: gridColor }, scale: first.key }] : []),
      ],
      series: [
        {},
        ...activeMetrics.map((m) => ({ label: m.label, stroke: m.color, width: 2, points: { show: false }, scale: m.key })),
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null) return;
            if (lastEmittedRef.current === idx) return;
            lastEmittedRef.current = idx;
            onCursorChangeRef.current(idx);
          },
        ],
        // Dragging on the chart zooms into the selected x span (uPlot's default behavior) but
        // doesn't offer a way back out on its own - track whether we're zoomed so a "Reset zoom"
        // control can appear, and to know when it can be hidden again.
        setScale: [
          (u, key) => {
            if (key !== "x") return;
            const xs = u.data[0];
            const xScale = u.scales.x;
            if (!xs || xs.length === 0 || !xScale) return;
            const isFullRange = xScale.min === xs[0] && xScale.max === xs[xs.length - 1];
            setZoomed(!isFullRange);
          },
        ],
      },
    };

    const u = new uPlot(opts, data, containerRef.current);
    uplotRef.current = u;

    // Restore the crosshair at the current position on the fresh instance (a
    // metric/theme switch destroys and recreates uPlot, which forgets it).
    const xVal = u.data[0]?.[cursorIndex];
    if (xVal !== undefined) {
      lastEmittedRef.current = cursorIndex;
      u.setCursor({ left: u.valToPos(xVal, "x"), top: 0 });
    }

    const onResize = () => {
      if (containerRef.current) u.setSize({ width: containerRef.current.clientWidth, height: 220 });
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      u.destroy();
      uplotRef.current = null;
    };
    // Recreated whenever the selected metrics or theme change; data updates are pushed via setData below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, resolved]);

  useEffect(() => {
    uplotRef.current?.setData(data);
  }, [data]);

  // Reflect externally-driven cursor changes (from the map or the scrubber) onto the chart.
  useEffect(() => {
    const u = uplotRef.current;
    if (!u || !u.data[0] || u.data[0].length === 0) return;
    if (lastEmittedRef.current === cursorIndex) return;
    const xVal = u.data[0][cursorIndex];
    if (xVal === undefined) return;
    lastEmittedRef.current = cursorIndex;
    const left = u.valToPos(xVal, "x");
    u.setCursor({ left, top: 0 });
  }, [cursorIndex]);

  function resetZoom() {
    const u = uplotRef.current;
    if (!u) return;
    const xs = u.data[0];
    if (!xs || xs.length === 0) return;
    u.setScale("x", { min: xs[0]!, max: xs[xs.length - 1]! });
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-night-800 dark:bg-night-900">
      <div className="mb-2 flex flex-wrap gap-1">
        {METRICS.map((m) => {
          const active = selected.has(m.key);
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggleMetric(m.key)}
              aria-pressed={active}
              style={active ? { backgroundColor: m.color } : undefined}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                active
                  ? "text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-night-800 dark:text-night-300 dark:hover:bg-night-700"
              }`}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      {zoomed && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={resetZoom}
            className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-200 dark:bg-night-800 dark:text-night-300 dark:hover:bg-night-700"
          >
            Reset zoom
          </button>
        </div>
      )}
      <div ref={containerRef} className="w-full" onDoubleClick={resetZoom} />
    </div>
  );
}
